// MIDAS V3 — Message feedback slice (Phase B — R5 sub-task 5a + 5b)
//
// V2 sibling: customer/midas/components/v2/midasMessageFeedbackV2Slice.ts
//   (byte-frozen — v3 layers POST-to-bot wiring on TOP of v2's local toggle slice
//   rather than mutating v2).
//
// Strategy: v3-only slice with a single createAsyncThunk (submitFeedback) that
//   POSTs to the MachineID proxy at /v1/bot/midas/feedback. Local Like/Dislike
//   visual toggle stays on the v2 slice (MessageActionsV3 dispatches both v2
//   toggle + this thunk on click).
//
// Strategy: scratch (no v2 thunks exist for this — v2 was a Phase 1 local-only
//   scratch slice; Phase 5 = R5 lands as this v3 slice).
// Verified no-MUI: yes (no JSX).
// Phase: R5 5a + 5b.
//
// Per MIDAS_V3_BUGS_0519_R5_SPEC.md sub-task 5 + Security Agent review:
//   • POST /api/account/v1/bot/midas/feedback (server-side proxy).
//   • Body: { message_id, thread_id, action: "like"|"dislike", comment?, source_wrong? }.
//   • thread_id is optional (older messages may not have an activeThreadId; bot
//     accepts null per API_CONTRACTS.md §"MIDAS Message Feedback").
//   • SEC-REQ-1/2/4 enforced server-side; SEC-REQ-3 enforced bot-side. Frontend
//     forwards only — no client-side scope check.
//
// Response handling:
//   • 200 → store { feedbackId, persistedAt }. Mark `success`.
//   • 409 (`duplicate_feedback`) → soft success. Already recorded; treat as
//     success without surfacing an error.
//   • 404 (`message_not_found`) → hard error. Caller (MessageActionsV3) reverts
//     the local toggle. The thunk rejects with a marker error string so the
//     component layer can detect it.
//   • 400/401/403/429 → logged warning. Local toggle is NOT reverted (the user's
//     intent stands; only the bot-side row didn't persist).
//
// State shape: per-message entry keyed by botMessageId. We key by botMessageId
//   (the bot-minted id) rather than the local ChatMessage.id because the
//   feedback API is keyed by bot id. Phase A landed the SSE extraction; this
//   slice consumes the result.
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import api from "../../../../../core/api";
import type { AxiosError } from "axios";

const getRequestConfig = () => ({
  baseURL: (window as any).app.config.apiBaseUrl,
});

// ============================================================
// Types
// ============================================================

export type FeedbackAction = "like" | "dislike";

/** Per-message entry, keyed by botMessageId. */
export interface FeedbackEntry {
  /** The action the user POSTed. Last-write-wins for the visible UI. */
  action: FeedbackAction;
  /** Bot-returned feedback row id. Present after a successful POST. */
  feedbackId?: string;
  /** Bot-returned persisted_at timestamp. Present after a successful POST. */
  submittedAt?: string;
  /** True between dispatch and fulfilled/rejected. UI may show a small spinner. */
  pending: boolean;
  /** Last error message, if any. NOT shown for 409 (treated as soft success). */
  error?: string;
  /** Whether a comment was sent with the POST (for the "Recorded" pill). */
  hasComment?: boolean;
  /** Whether a source-wrong value was sent (dislike-only). */
  hasSourceWrong?: boolean;
}

interface MidasMessageFeedbackV3State {
  /** Keyed by botMessageId. */
  byMessageId: Record<string, FeedbackEntry>;
}

const initialState: MidasMessageFeedbackV3State = {
  byMessageId: {},
};

// ============================================================
// Thunk: submitFeedback
// ============================================================

export interface SubmitFeedbackArgs {
  /** Bot-minted message_id from SSE answer/done events (ChatMessage.botMessageId). */
  messageId: string;
  /** activeThreadId from midasPageSlice. Optional — bot accepts null. */
  threadId?: string | null;
  /** "like" or "dislike". Regenerate is NOT routed through this thunk — see
   *  spec sub-task 5a, regenerate stays local. */
  action: FeedbackAction;
  /** Optional user-typed comment (≤2000 chars; server-enforced cap). */
  comment?: string;
  /** Dislike-only. true / false / undefined-or-null. Server rejects `source_wrong`
   *  on like/regenerate with 400 `invalid_source_wrong_action`. */
  sourceWrong?: boolean | null;
}

interface SubmitFeedbackResponse {
  feedback_id: string;
  action: string;
  persisted_at: string;
}

/**
 * Special rejection reason that signals the caller to revert local toggle state.
 * Component layer matches on this exact string.
 */
export const FEEDBACK_REJECT_MESSAGE_NOT_FOUND = "message_not_found";

export const submitFeedback = createAsyncThunk<
  {
    messageId: string;
    action: FeedbackAction;
    feedbackId?: string;
    persistedAt?: string;
    softDuplicate: boolean;
    hasComment: boolean;
    hasSourceWrong: boolean;
  },
  SubmitFeedbackArgs,
  { rejectValue: string }
>(
  "midasMessageFeedbackV3/submit",
  async (args, { rejectWithValue }) => {
    // Build body — omit `comment` when empty, omit `source_wrong` when null/undefined.
    const body: Record<string, unknown> = {
      message_id: args.messageId,
      action: args.action,
    };
    if (args.threadId) {
      body.thread_id = args.threadId;
    }
    const hasComment =
      typeof args.comment === "string" && args.comment.trim().length > 0;
    if (hasComment) {
      body.comment = args.comment;
    }
    const hasSourceWrong =
      args.action === "dislike" &&
      (args.sourceWrong === true || args.sourceWrong === false);
    if (hasSourceWrong) {
      body.source_wrong = args.sourceWrong;
    }

    try {
      const response = await api.post<SubmitFeedbackResponse>(
        "/v1/bot/midas/feedback",
        body,
        getRequestConfig()
      );
      return {
        messageId: args.messageId,
        action: args.action,
        feedbackId: response.data?.feedback_id,
        persistedAt: response.data?.persisted_at,
        softDuplicate: false,
        hasComment,
        hasSourceWrong,
      };
    } catch (err) {
      const axiosErr = err as AxiosError<{ error?: string; message?: string }>;
      const status = axiosErr.response?.status ?? 0;
      const errorCode = axiosErr.response?.data?.error;

      // 409 duplicate_feedback → soft success. The row already exists on the
      // bot. Treat as success so the UI shows the action as recorded.
      if (status === 409) {
        return {
          messageId: args.messageId,
          action: args.action,
          softDuplicate: true,
          hasComment,
          hasSourceWrong,
        };
      }

      // 404 message_not_found → hard error. Caller reverts local toggle.
      if (status === 404) {
        // eslint-disable-next-line no-console
        console.warn(
          "[midas-v3-feedback] message_not_found for botMessageId",
          args.messageId
        );
        return rejectWithValue(FEEDBACK_REJECT_MESSAGE_NOT_FOUND);
      }

      // 400/401/403/429 → log + soft fail (no UI revert). Surface a generic
      // error message in the slice so a future "retry" UX can read it.
      // eslint-disable-next-line no-console
      console.warn(
        "[midas-v3-feedback] submit failed",
        { status, errorCode, messageId: args.messageId, action: args.action }
      );
      return rejectWithValue(errorCode || `http_${status || "unknown"}`);
    }
  }
);

// ============================================================
// Slice
// ============================================================

const slice = createSlice({
  name: "midasMessageFeedbackV3",
  initialState,
  reducers: {
    /** Clear the stored feedback row for a message — used when the local v2
     *  toggle goes back OFF and we want the "Recorded" pill to dismiss. */
    clearFeedback: (state, action: PayloadAction<{ messageId: string }>) => {
      delete state.byMessageId[action.payload.messageId];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitFeedback.pending, (state, action) => {
        const messageId = action.meta.arg.messageId;
        const prev = state.byMessageId[messageId];
        state.byMessageId[messageId] = {
          ...(prev || { action: action.meta.arg.action, pending: true }),
          action: action.meta.arg.action,
          pending: true,
          error: undefined,
        };
      })
      .addCase(submitFeedback.fulfilled, (state, action) => {
        const { messageId, feedbackId, persistedAt, hasComment, hasSourceWrong } =
          action.payload;
        state.byMessageId[messageId] = {
          action: action.payload.action,
          feedbackId,
          submittedAt: persistedAt,
          pending: false,
          hasComment,
          hasSourceWrong,
        };
      })
      .addCase(submitFeedback.rejected, (state, action) => {
        const messageId = action.meta.arg.messageId;
        const reason =
          (action.payload as string | undefined) ||
          action.error.message ||
          "unknown";
        // On message_not_found we drop the entry entirely so the caller can
        // revert the local toggle without leaving a stale "pending" row.
        if (reason === FEEDBACK_REJECT_MESSAGE_NOT_FOUND) {
          delete state.byMessageId[messageId];
          return;
        }
        const prev = state.byMessageId[messageId];
        state.byMessageId[messageId] = {
          ...(prev || { action: action.meta.arg.action, pending: false }),
          action: action.meta.arg.action,
          pending: false,
          error: reason,
        };
      });
  },
});

export const { clearFeedback } = slice.actions;
export default slice.reducer;
